import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'wsm-common-data';
import logger from 'ws-scripts/modules/logger';
import { useFetchLocationGeoZip } from '../hooks/useFetchLocationGeoZip';
import { useLocationsData } from '../hooks/useLocationsData';
import { usePostalCode } from '../hooks/usePostalCode';
import '../global-sass/main.scss';

const LOG_PREFIX = '[ws-locations-control][SearchResultCount]';
const LOCATION_SEARCH_RANGE = 50;
const EXCLUDE_BY_RANGE = true;

// SearchResultCount - Integrated with ws-locations-data

const SearchResultCount = ({
	isApiReady,
	sessionToken,
	resetSession,
	selectedPlaceId,
	onLocationStatusChange
}) => {
	const labels = useLabels();
	const [locationCountString, setLocationCountString] = useState('');
	const {
		zipcode: geoZip,
		coordinates,
		isLoading: geoLoading,
		error: geoError
	} = useFetchLocationGeoZip(
		isApiReady,
		sessionToken,
		resetSession,
		selectedPlaceId
	);
	const postalCode = usePostalCode();
	const {
		locationCount,
		error: locationError,
		isLoading: locationLoading,
		isReady: isLocationsDataReady,
		setSearchParams,
		currentPostalCode
	} = useLocationsData();

	// Determine the active postal code
	const activePostalCode = geoZip || postalCode;

	logger.debug(`${LOG_PREFIX} State`, {
		isReady: isLocationsDataReady,
		count: locationCount,
		activePostalCode,
		currentPostalCode,
		coordinates
	});

	// Publish coordinates to ws-locations-data for map rendering
	useEffect(() => {
		if (!coordinates) {
			return;
		}

		if (window?.DDC?.LocationsData?.setLocationWithCoordinates) {
			window.DDC.LocationsData.setLocationWithCoordinates({
				coordinates
			});
			logger.info(
				`${LOG_PREFIX} Published coordinates via pubsub:`,
				coordinates
			);
		} else {
			logger.warn(`${LOG_PREFIX} LocationsData pubsub not available`);
		}
	}, [coordinates]);

	// Reset to default NY coordinates when search is cleared
	useEffect(() => {
		if (!selectedPlaceId && !geoLoading && !geoError) {
			// No place selected and no loading/error, reset to default NY coordinates
			const defaultNYCoordinates = {
				lat: 40.7128,
				lng: -74.006
			};

			if (window?.DDC?.LocationsData?.setLocationWithCoordinates) {
				window.DDC.LocationsData.setLocationWithCoordinates({
					coordinates: defaultNYCoordinates
				});
				logger.info(
					`${LOG_PREFIX} Reset to default NY coordinates:`,
					defaultNYCoordinates
				);
			}
		}
	}, [selectedPlaceId, geoLoading, geoError]);

	// Trigger location fetch only when postal code actually changes
	useEffect(() => {
		if (
			isLocationsDataReady &&
			activePostalCode &&
			activePostalCode !== currentPostalCode
		) {
			setSearchParams(
				activePostalCode,
				LOCATION_SEARCH_RANGE,
				EXCLUDE_BY_RANGE
			);
		}
	}, [
		isLocationsDataReady,
		activePostalCode,
		currentPostalCode,
		setSearchParams
	]);

	useEffect(() => {
		const hasLocations = !geoError && !locationError && locationCount > 0;

		if (geoError || locationError || !locationCount) {
			setLocationCountString(labels.get('NO'));
			// selectedPlaceId indicates a search has been made
		} else {
			setLocationCountString(locationCount);
		}

		// Notify parent component about location availability
		if (onLocationStatusChange) {
			onLocationStatusChange(hasLocations);
		}
	}, [
		locationCount,
		geoError,
		locationError,
		labels,
		selectedPlaceId,
		onLocationStatusChange
	]);

	// Don't show count while loading or when locationCountString is empty
	if (geoLoading || locationLoading || locationCountString === '') {
		return null;
	}

	if (!isLocationsDataReady && locationCount === 0) {
		logger.debug(
			`${LOG_PREFIX} Not rendering - ws-locations-data not ready and no location count`
		);
		return null;
	}

	logger.debug(`${LOG_PREFIX} Rendering with count: ${locationCountString}`);

	return (
		<h3 className="location-count text-body">{`${locationCountString} ${labels
			.get('LOCATIONS_NEAR_YOU')
			.toLowerCase()}`}</h3>
	);
};

SearchResultCount.propTypes = {
	isApiReady: PropTypes.bool,
	sessionToken: PropTypes.string,
	resetSession: PropTypes.func,
	selectedPlaceId: PropTypes.string,
	onLocationStatusChange: PropTypes.func
};

export default SearchResultCount;
